import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import HighlightedInput from '../HighlightedInput';
import { REPLACEMENTS_KEYS } from '../../helpers/tableKeys';
import {
  REPLACED_PATTERN,
  REPLACEMENT_PATTERN,
} from '../../helpers/validationPatterns';
import { normalizeReplacementValue } from '../../helpers/replacementsNormalization';

const { REPLACED, REPLACEMENT } = REPLACEMENTS_KEYS;
const SEMICOLON_REPLACEMENT_TYPES = new Set([
  'PHRASE',
  'PHRASE_SEP',
  'PHRASE_EXC',
  'SENTENCE_SEP',
  'SENTENCE_EXC',
]);

const EditableHighlightedCell = ({
  cell,
  onChangeData,
  value: initialValue,
  lang,
  replacementType,
}) => {
  const {
    row: { original },
    column: { id: columnName },
  } = cell;

  const [value, setValue] = useState(initialValue || '');
  const [error, setError] = useState('');

  const { t } = useTranslation(['promptbar']);

  useEffect(() => {
    setValue(initialValue || '');
  }, [initialValue]);

  const replacedPattern = SEMICOLON_REPLACEMENT_TYPES.has(replacementType)
    ? /^(?!.* {2})[a-zа-яё0-9№; ]*$/i
    : REPLACED_PATTERN;

  const schema = Yup.object().shape({
    [REPLACED]: Yup.string()
      .matches(replacedPattern, t('incorrectFormat'))
      .required(t('requiredField')),
    [REPLACEMENT]: Yup.string().matches(
      REPLACEMENT_PATTERN,
      t('incorrectFormat'),
    ),
  });

  const onChange = event => {
    setError('');
    setValue(event.target.value);
  };

  const handleValidate = async nextValue => {
    const fieldSchema = schema.fields[columnName];
    if (!fieldSchema) return true;

    try {
      await fieldSchema.validate(nextValue, { abortEarly: false });
      setError('');
      return true;
    } catch (errors) {
      let validationError = '';
      errors.inner?.forEach(validationItem => {
        validationError = validationItem.message;
      });
      setError(validationError);
      return false;
    }
  };

  const onBlur = async () => {
    const normalized = normalizeReplacementValue(columnName, value);
    setValue(normalized);

    const isFormValid = await handleValidate(normalized);

    onChangeData({
      index: original.uniqId,
      value: normalized.trim(),
      columnName,
      isValid: isFormValid,
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      <HighlightedInput
        value={value}
        onChange={onChange}
        lang={lang}
        error={Boolean(error)}
        minRows={1}
        inputProps={{
          name: columnName,
          onBlur,
          onKeyDown: event => {
            if (event.key === 'Enter') {
              event.preventDefault();
            }
          },
        }}
      />
    </Box>
  );
};

EditableHighlightedCell.propTypes = {
  cell: PropTypes.object.isRequired,
  onChangeData: PropTypes.func.isRequired,
  value: PropTypes.string,
  lang: PropTypes.string,
  replacementType: PropTypes.string,
};

export default EditableHighlightedCell;
